// ControleHistogramModal.jsx
import React, { useMemo } from "react";
import { Modal, Button, Badge } from "react-bootstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  Label,
  Cell
} from "recharts";

export default function ControleHistogramModal({
  show,
  onHide,
  valores = [],
  limites = [],
  estatisticas = {},
  unidade = "",
  titulo = "Histograma do Controle",
}) {
  const toNumber = (v) =>
    typeof v === "number" ? v : Number(String(v).replace(",", "."));

  // extrai LSL/USL
  const { LSL, USL } = useMemo(() => {
    const nums = (limites || [])
      .map((l) => toNumber(l?.valor))
      .filter(Number.isFinite);
    if (!nums.length) return { LSL: null, USL: null };
    return { LSL: Math.min(...nums), USL: Math.max(...nums) };
  }, [limites]);

  // agrupa por espaços de 5
  const { bins, min, max, ticks } = useMemo(() => {
    const xs = (valores || []).map(toNumber).filter(Number.isFinite);
    if (!xs.length) return { bins: [], min: 0, max: 1, ticks: [] };

    const mn = Math.min(...xs);
    const mx = Math.max(...xs);
    const n = xs.length;
    const k = Math.max(5, Math.ceil(Math.log2(n) + 1));
    const width = (mx - mn) / (k || 1);
    const edges = Array.from({ length: k + 1 }, (_, i) => mn + i * width);
    const counts = Array(k).fill(0);

    for (const x of xs) {
      let idx = Math.floor((x - mn) / (width || 1));
      if (idx < 0) idx = 0;
      if (idx >= k) idx = k - 1;
      counts[idx]++;
    }

    const data = counts.map((c, i) => {
      const start = edges[i];
      const end = edges[i + 1];
      const center = (start + end) / 2;
      return {
        start,
        end,
        center,
        label: `${start.toFixed(2)}–${end.toFixed(2)}`,
        count: c,
      };
    });

    return {
      bins: data,
      min: mn,
      max: mx,
      ticks: data.map((b) => b.center),
    };
  }, [valores]);

  const media = Number(estatisticas?.media);
  const cpk = Number(estatisticas?.cpk);

  const cpkBadgeVariant = (v) => {
    if (!Number.isFinite(v)) return "secondary";
    if (v >= 1.33) return "success";
    if (v >= 1.0) return "warning";
    return "danger";
  };

  const findLabelByCenter = (center) => {
    const b = bins.reduce((best, cur) =>
      Math.abs(cur.center - center) < Math.abs((best?.center ?? Infinity) - center)
        ? cur
        : best,
    null);
    return b?.label ?? center.toFixed(2);
  };

  const refLabel = (text, color) => ({
    value: text,
    position: "top",
    fill: color,
    dy: -6,
  });

  const hasMedia = Number.isFinite(media);
  const hasLSL = Number.isFinite(LSL);
  const hasUSL = Number.isFinite(USL);

  // garante que o domínio inclua limites
  const domainMin = Math.min(
    min,
    hasLSL ? LSL : min
  );
  const domainMax = Math.max(
    max,
    hasUSL ? USL : max
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
          <Badge bg="secondary">
            Amostras: {Array.isArray(valores) ? valores.length : 0}
          </Badge>
          {hasMedia && (
            <Badge bg="info">
              Média {media.toFixed(3)} {unidade || ""}
            </Badge>
          )}
          {hasLSL && (
            <Badge bg="dark">
              LSL {LSL.toFixed(3)} {unidade || ""}
            </Badge>
          )}
          {hasUSL && (
            <Badge bg="dark">
              USL {USL.toFixed(3)} {unidade || ""}
            </Badge>
          )}
          <Badge bg={cpkBadgeVariant(cpk)}>
            Cpk {Number.isFinite(cpk) ? cpk.toFixed(3) : "-"}
          </Badge>
        </div>

        <div style={{ width: "100%", height: 380 }}>
          <ResponsiveContainer>
            <BarChart
              data={bins}
              margin={{ top: 40, right: 30, bottom: 70, left: 16 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="center"
                domain={[domainMin, domainMax]}
                ticks={ticks}
                tickFormatter={findLabelByCenter}
                angle={-30}
                textAnchor="end"
                height={60}
                allowDataOverflow
              >
                <Label
                  value={unidade ? `Valor (${unidade})` : "Valor"}
                  offset={10}
                  position="insideBottom"
                />
              </XAxis>
              <YAxis allowDecimals={false}>
                <Label value="Frequência" angle={-90} position="insideLeft" />
              </YAxis>
              <Tooltip
                labelFormatter={(val) => `Centro: ${Number(val).toFixed(3)}`}
                formatter={(v) => [v, "Frequência"]}
                cursor={{ fill: 'rgba(0,0,0,0.05)' }}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {bins.map((entry, index) => {
                  const isLow = hasLSL && entry.end <= LSL;
                  const isHigh = hasUSL && entry.start >= USL;
                  const color = (isLow || isHigh) ? "#dc3545" : "#0d6efd";
                  return <Cell key={`cell-${index}`} fill={color} />;
                })}
              </Bar>
              {hasMedia && (
                <ReferenceLine
                  x={media}
                  stroke="#0dcaf0"
                  strokeDasharray="4 4"
                  label={refLabel(`Média ${media.toFixed(2)} ${unidade || ""}`, "#0dcaf0")}
                  ifOverflow="extendDomain"
                />
              )}
              {hasLSL && (
                <ReferenceLine
                  x={LSL}
                  stroke="#198754"
                  label={refLabel(`LSL ${LSL.toFixed(2)} ${unidade || ""}`, "#198754")}
                  ifOverflow="extendDomain"
                />
              )}
              {hasUSL && (
                <ReferenceLine
                  x={USL}
                  stroke="#198754"
                  label={refLabel(`USL ${USL.toFixed(2)} ${unidade || ""}`, "#198754")}
                  ifOverflow="extendDomain"
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}